import { colors } from '@styleseat/brand';
import store from '../../../store';
import Analytics from '../../../modules/analytics';
import { getCellPhone } from '../../../modules/provider/ProClientList';
import { getSharingLinks } from '../../consumer/sharing/social/helpers';
import { openExternalUrl } from '../../../modules/openExternalUrl';
import { generateSmsUrl } from '../../consumer/sharing/social/sms';
import ROCKET_ICON from '../../../../images/icons/icon-rocket.svg';

import './FirstClientAddedSnackbar.scss';
import { LAST_INVITED_TO_BOOK, updateSelfPromotionDates } from '../../../modules/Api';
import { ShareTarget } from '../../../modules/share';
import { PRO_CLIENT_VIEW } from '../../../route-names';

/**
 * Snackbar shown as a celebration for adding pro's first client
 */
const FirstClientAddedSnackbar = {
  /**
   * Close the snackbar
   */
  closeSnackbar() {
    store.dispatch.snackbar.close();
  },

  /**
   * Open snackbar.
   * @param {array} clientList - list of clients added
   * @param {number} providerId - the provider Id
   */
  open(clientList, providerId) {
    const client = clientList[0];
    const cellPhone = getCellPhone(client.phones);
    const singleClientConfirmText = cellPhone ? 'Invite To Book' : 'add-info';
    const confirmText = clientList.length > 1 ? 'Got It' : singleClientConfirmText;
    const dismissText = clientList.length > 1 ? '' : 'Dismiss';
    const subHeader = clientList.length > 1
      ? `You added ${
        clientList.length
      } clients to your client list. Now invite one to book with you.`
      : 'You added 1 client to your client list. Now invite one to book with you.';

    const iconProps = {
      uri: ROCKET_ICON,
      size: 36,
      animate: true,
    };

    /**
     * Will open native messaging app on app to send pre-populated message
     */
    const sendSMSToClient = () => {
      const trackingClickEventName = cellPhone
        ? 'pro_one_client_added_snackbar_celebration_invite_to_book_clicked'
        : 'pro_one_client_added_snackbar_celebration_add_info_clicked';

      Analytics.track(trackingClickEventName);
      updateSelfPromotionDates(providerId, [LAST_INVITED_TO_BOOK]);

      getSharingLinks(ShareTarget.PROFILE, providerId, true, [
        {
          id: 'sms',
          parameters: [
            { key: 'utm_medium', value: 'sms' },
            { key: 'utm_source', value: 'client_invite_from_pro_snack_bar' },
          ],
        },
      ]).then(links => {
        const body = `Book your next appointment with me on StyleSeat! It's super easy, plus you can see a calendar view of all my available times. ${
          links.sms
        }`;
        const url = generateSmsUrl(cellPhone.number, body);
        openExternalUrl(url, null);
      });
    };

    /**
     * Routes to detailed client view if client does not have a cell phone
     */
    const goToAddClientInfo = async () => {
      store.dispatch.route.go({
        route: PRO_CLIENT_VIEW,
        params: { clientId: client.id },
        replace: true,
      });
      this.closeSnackbar();
    };

    /**
     * Click handler if a single client is added
     */
    const handleSingleClientConfirmClick = cellPhone
      ? sendSMSToClient
      : goToAddClientInfo;

    /**
     * Click handler if multiple clients imported
     */
    const handleGotItClick = () => {
      Analytics.track(
        'pro_many_client_added_snackbar_celebration_got_it_clicked',
      );
      this.closeSnackbar();
    };

    /**
     * Click handler for the confirm button
     */
    let handleConfirmClick;
    if (clientList.length > 1) {
      handleConfirmClick = handleGotItClick;
    } else {
      handleConfirmClick = handleSingleClientConfirmClick;
    }

    const snackBarProps = {
      className: 'first-client-added-snackbar',
      inlineHighlight: 'Nice!',
      subHeader,
      iconProps,
      sideColor: colors.whiteWash,
      confirm: {
        text: confirmText,
        action: handleConfirmClick,
      },
      dismiss: {
        text: dismissText,
        action: () => {
          Analytics.track(
            'pro_one_client_added_snackbar_celebration_invite_to_book_dismissed',
          );
          this.closeSnackbar();
        },
      },
    };

    const trackingViewEventName = clientList.length > 1
      ? 'pro_many_client_added_snackbar_celebration_viewed'
      : 'pro_one_client_added_snackbar_celebration_viewed';

    Analytics.track(trackingViewEventName);
    store.dispatch.snackbar.open(snackBarProps);
  },
};

export default FirstClientAddedSnackbar;
